import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import Classes from "model";
import { CheckingAccount, CODAccount, LoanAccount, LOCAccount, Profile, SavingsAccount, UnlinkedAccount } from "../";

/**
* @class Account
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.accounts[0];
* const account = new Account(record);
* @property {NUMBER} _id - The id of the Account
* @property {STRING} _name - The name of the account
* @property {STRING} _nickname - The custom name for the account
* @property {STRING} _number - the last four of account
* @property {STRING} _type - represents the account type
* @property {NUMBER} _typeID - ID of sub type for additional information
* @property {BOOLEAN} _canTransferFrom - can the account be transferred from
* @property {bcmsDate} _dateOpened - days in past account was opened
* @property {BOOLEAN} _isMobilePrimary - is this the primary mobile account
* @property {bcmsDate} _lastDownloadDate - number of days in the past for last download
* @property {STRING} _balanceLabel - The label for the balance
* @property {STRING} _secondaryBalanceLabel - The label for the secondary balance
* @property {STRING} _statementPreference - The statement preference of the account
* @property {BOOLEAN} _isExternal - The external value of the account
* @property {STRING} _institutionName - The institution name of the account
* @property {STRING} _routingNumber - The routing number of the account
* @property {STRING} _externalStatus - The external status of the account
* @property {BOOLEAN} _isBusiness - The business value of the account
* @property {Profile} _profile - The associated profile of the account
* @property {STRING} _showDesktopAvailableBalance - The "desktop available balance" value of the account.
* @property {STRING} _showDesktopCurrentBalance - The "desktop current balance" value of the account.
* @property {STRING} _showMobileAvailableBalance - The "mobile available balance" value of the account.
* @property {STRING} _showMobileCurrentBalance - The "mobile current balance" value of the account.

*
* @description When a new "Account" is created in the model xml, 
* create a "mobileNickname" entry in the model xml as well.
* This is probably how the actual product works since the user 
* can't create a "Mobile Nickname" in the product,
* but they have access to mobile nickname entries they can edit.
* 
*/

export default class Account extends BCMSRecord {
  _id
  _name
  _nickname
  _number
  _type
  _canTransferFrom
  _dateOpened
  _isMobilePrimary
  _lastDownloadDate
  _balanceLabel
  _secondaryBalanceLabel
  _statementPreference
  _isExternal
  _institutionName
  _routingNumber
  _externalStatus
  _isBusiness
  _profile
  _showDesktopAvailableBalance
  _showDesktopCurrentBalance
  _showMobileAvailableBalance
  _showMobileCurrentBalance

  constructor(record) {
    super(record)
  }

  /** 
  * @function hasConsumerAccounts   
  * @static
  * @property {VOID} - No parameters
  * @description Returns true if we have branding option "Show Consumer Accounts" set to "Yes".
  * @example const hasConsumerAccounts = Account.hasConsumerAccounts(brandingObj);
  * @memberof Account 
  */
  static hasConsumerAccounts(branding) {
    return branding.options.show_consumer_accts_abi22 === "true";
  }

  /** 
  * @function hasBusinessAccounts   
  * @static
  * @property {VOID} - No parameters
  * @description Returns true if we have branding option "Show Business Accounts" set to "Yes".
  * @example const hasBusinessAccounts = Account.hasBusinessAccounts(brandingObj);
  * @memberof Account 
  */
  static hasBusinessAccounts(branding) {
    return branding.options.show_business_accounts_abi22 === "true";
  }

  /**
   * @name businessOnlyBrandingErrorMessage
   * @static
   * @property {STRING} - The error message to display when the branding option "Show Business Accounts" is not set to "Yes".
   * @description Returns the error message to display when the branding option "Show Business Accounts" is not set to "Yes".
   * @example const businessOnlyBrandingErrorMessage = Account.businessOnlyBrandingErrorMessage;
   * @memberof Account 
   */
  static businessOnlyBrandingErrorMessage = `This capture is only shown when branding option "Show Business Accounts" 
  (action script name: show_business_accounts_abi22) is set to "Yes".`

  /** 
  * @function hasConsumerAndBusinessAccounts   
  * @static
  * @property {VOID} - No parameters
  * @description Returns true if we have branding options
  * "Show Business Accounts" and"Show Consumer Accounts" both set to "Yes".
  * @example const hasConsumerAndBusinessAccounts = Account.hasConsumerAndBusinessAccounts(brandingObj);
  * @memberof Account 
  */
  static hasConsumerAndBusinessAccounts(branding) {
    const { options } = branding;
    return options.show_consumer_accts_abi22 === "true" && options.show_business_accounts_abi22 === "true";
  }

  /**    
  * @function showBalanceTypeDependingOnViewType   
  * @static
  * @property { Account } - Instance of "Account".
  * @property { STRING } - Account balance type, either "available" or "current".
  * @property { STRING } - Current window view type, either "desktop" or "mobile".
  * @description Returns a boolean indicating whether or not to show "Available Balance" or "Current Balance",
  * which depends on branding in the model xml and the current view ("desktop" or "mobile").
  * @example  const hasAvailableBalanceOn = Account.showBalanceTypeDependingOnViewType(account, "available", isDesktop ? "desktop" : "mobile");
  * @memberof Account 
  */
  static showBalanceTypeDependingOnViewType(account, balanceType, viewType) {
    let _type = balanceType.toLowerCase();
    _type = _type.charAt(0).toUpperCase() + _type.slice(1);
    const isDesktopView = viewType.toLowerCase() === "desktop";
    const isMobileView = !isDesktopView;
    return (isDesktopView && account[`showDesktop${ _type }Balance`] === "true") ||
    (isMobileView && account[`showMobile${ _type }Balance`] === "true");
  }

  /**    
  * @property getDefaultBrandedAccountByType - 
  * @description Returns an default account that accounts for consumer and business branding options.
  * Because this function is used throughout the code, it has to support the switch between asset accounts
  * and line of credit accounts when branding option "Use Line of Credit as Primary" is set to "Yes".
  * @example const account = Account.getDefaultBrandedAccountByType("checking", "business", branding);
  * @memberof Account 
  */
  static getDefaultBrandedAccountByType(type, branding) {
    const { options } = branding ?? this._GlobalController.state.branding;
    const showConsumerAccounts = options.show_consumer_accts_abi22 === "true";
    const hasLOCAccounts = options.loc_primary_abi22 === "true";
    let account;

    if(type === "checking") {
      if(hasLOCAccounts) {
        account = showConsumerAccounts ? Account.initByType("9") : Account.initByType("10");
      } else {
        account = showConsumerAccounts ? Account.initByType("0") : Account.initByType("6");
      }
    } else if(type === "loan") {
      account = showConsumerAccounts ? Account.initByType("3") : Account.initByType("8");
    } else if(type === "savings") {
      if(hasLOCAccounts) {
        account = showConsumerAccounts ? Account.initByType("3") : Account.initByType("8");
      } else {
        account = showConsumerAccounts ? Account.initByType("1") : Account.initByType("5");
      }
    } else {
      account = showConsumerAccounts ? Account.initByType("0") : Account.initByType("6");
    }

    return account;
  }

  /** 
  * @function idIsOfType   
  * @static
  * @property {STRING} - An account id
  * @description Returns the type of account that the id is referencing. 
  * Needed in pages where an id is used in a url like 
  * "/accountdetails/0" where "0" is an invalid account id due to branding.
  * @example const idIsOfType = Account.idIsOfType();
  * @memberof Account 
  */
  static idIsOfType(id) {
    let type = "";
    
    if(id === "0" || id === "6") {
      type = "checking";
    } else if(id === "1" || id === "5") {
      type = "savings";
    } else if(id === "2" || id === "7") {
      type = "cod";
    } else if(id === "3" || id ==="8") {
      type = "loan";
    } else if(id === "9" || id === "10") {
      type = "loc";
    } else {
      type = "checking";
    }
    return type;
  }

  /**    
  * @property id {STRING} - Returns the account for the appropriate type
  * @example const account = Account.initByType("1");
  * @memberof Account 
  */
  static initByType(id) {
    let account = Account.initByID(id);
    if(!account) {
      return undefined;
    }

    switch (account.type) {
      case "checking":
        return CheckingAccount.initByID(id);
      case "savings":
        return SavingsAccount.initByID(id);
      case "cod":
        return CODAccount.initByID(id);
      case "loan":
        return LoanAccount.initByID(id);
      case "loc":
        return LOCAccount.initByID(id);
      case "unlinked":
        return UnlinkedAccount.initByID(id);
      default:
        return undefined;
    }
  }

  /** 
  * @function initAll   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all accounts with branding option "Show Business Accounts" 
  * (action script name: show_business_accounts_abi22) applied.
  * @example const accounts = Account.initAll();
  * @memberof Account 
  */
  static initAll() {
    const { options } = this._GlobalController.state.branding;
    const showBusinessAccounts = options.show_business_accounts_abi22 === "true";

    const filterByBusinessValue = item => {
      if(showBusinessAccounts) {
        return true;
      } else {
        if(item.isBusiness === "true") {
          return false;
        }
      }
      return true;
    };

    const accounts = this._GlobalController.state[this._RecordType].filter(filterByBusinessValue);

    const initAccounts = accounts.map((record) => {
        return this.initByType(record.id);
    }).filter(item => item && item.type !== "unlinked");
    
    return  initAccounts.sort((a, b) => {
      const typeOrder = ["checking", "savings", "loc", "cod", "loan", "unlinked"];
  
      // First, move all non-business accounts first
      if (a.isBusiness !== b.isBusiness) return a.isBusiness - b.isBusiness;

      // If both are the same in isBusiness, sort by type order
      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
  }
  
  /** 
  * @function initAllIncludingUnlinked   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all accounts similar to "Account.initAll()", but we include
  * unlinked accounts. This function is used in the "Make A Transfer" "To" select field
  * and the "Quick Transfer" "To" select field.
  * @example 
  * const initAllIncludingUnlinked = Account.initAllIncludingUnlinked();
  * @memberof Account 
  */
  static initAllIncludingUnlinked() {
        const { options } = this._GlobalController.state.branding;
    const showBusinessAccounts = options.show_business_accounts_abi22 === "true";
    const hasTransfersToCODAccounts = options.transfer_to_cd_abi22 !== "false";

    const filterByBusinessValue = item => {
      if(showBusinessAccounts) {
        return true;
      } else {
        if(item.isBusiness === "true") {
          return false;
        }
      }
      return true;
    };

    const accounts = this._GlobalController.state[this._RecordType].filter(filterByBusinessValue);

    let initAccounts = accounts.map((record) => {
        return this.initByType(record.id);
    }).filter(Boolean);

    if(!hasTransfersToCODAccounts) {
      initAccounts = initAccounts.filter(account => {
        return account.type !== "cod";
      });
    }
    
    return initAccounts.sort((a, b) => {
      const typeOrder = ["checking", "savings", "loc", "cod", "loan", "unlinked"];
  
      // First, move all non-business accounts first
      if (a.isBusiness !== b.isBusiness) return a.isBusiness - b.isBusiness;

      // If both are the same in isBusiness, sort by type order
      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
  }

    /** 
  * @function initAllBusinessAsset   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all business asset accounts. 
  * However, the addition of branding option "Use Line of Credit as Primary"
  * has added additional logic. If "Use Line of Credit as Primary" is set to "Yes", those asset accounts 
  * as switched with line of credit accounts.
  * @example const accounts = Account.initAllBusinessAsset();
  * @memberof Account 
  */
  static initAllBusinessAsset() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => item.isBusiness === "true" || item.type === "loc")
    .filter(item => item.type === "checking" || item.type === "savings" || item.type === "loc")
    .map((record) => {
        return this.initByType(record.id);
    }).filter(Boolean);
  }

  /** 
  * @function initAllBusinessAssetOnly   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all business asset accounts. 
  * @example const accounts = Account.initAllBusinessAsset();
  * @memberof Account 
  */
  static initAllBusinessAssetOnly() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => item.isBusiness === "true")
    .filter(item => item.type === "checking" || item.type === "savings" || item.type === "loc")
    .map((record) => {
        return this.initByType(record.id);
    }).filter(Boolean);
  }

    /** 
  * @function initAllBusinessAssetOnly   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all business asset accounts. 
  * @example const accounts = Account.initAllBusinessAsset();
  * @memberof Account 
  */
  static initAllBusinessChecking() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => item.isBusiness === "true" && item.type === "checking")
    .map((record) => {
        return this.initByType(record.id);
    }).filter(Boolean);
  }

  /** 
  * @function initAllAsset   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all asset accounts (checking or savings, not loans).
  * However, the addition of branding option "Use Line of Credit as Primary"
  * has added additional logic. If "Use Line of Credit as Primary" is set to "Yes", those asset accounts 
  * as switched with line of credit accounts.
  * @example const accounts = Account.initAllAsset();
  * @memberof Account 
  */
  static initAllAsset() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    
    return accounts.filter(item => {
      return (item.type === "checking" || item.type === "savings" || item.type === "loc")
    })
    .map((record) => {
        return this.initByType(record.id);
    });
  }

  /** 
  * @function initAllWithoutCOD   
  * @static
  * @property {VOID} - No parameters
  * @description Returns all accounts without COD accounts.
  * @example const accounts = Account.initAllWithoutCOD();
  * @memberof Account 
  */
  static initAllWithoutCOD() {
    const accounts = this._GlobalController.state[this._RecordType];
    
    return accounts.filter(item => {
      return item.type !== "cod" && item.type !== "unlinked";
    })
    .map((record) => {
        return this.initByType(record.id);
    });
  }

  /** 
  * @function hasLOCAccounts   
  * @static
  * @property {VOID} - No parameters
  * @description Returns a boolean telling us if branding option
  * "Use Line of Credit as Primary" is set to "Yes".
  * @example const hasLOCAccounts = Account.hasLOCAccounts();
  * @memberof Account 
  */
  static hasLOCAccounts() {
    const { options } = this._GlobalController.state.branding;
    return options.loc_primary_abi22 === "true";
  }

  /** 
  * @function isLoanOrLOC   
  * @static
  * @property {VOID} - No parameters
  * @description Returns a boolean telling us if the account
  * in the argument is of type loan or line of credit.
  * @example const hasLOCAccounts = Account.hasLOCAccounts();
  * @memberof Account 
  */
  static isLoanOrLOC(account) {
    return account.type === "loan" || account.type === "loc";
  }

  /** 
  * @function isLOC   
  * @static
  * @property {VOID} - No parameters
  * @description Returns a boolean telling us if the account
  * in the argument is of type line of credit.
  * @example const isLOC = Account.isLOC(account);
  * @memberof Account 
  */
  static isLOC(account) {
    return account.type === "loc";
  }

  /** 
  * @function isUnlinked   
  * @static
  * @property {Account} - An instance of "Account", or a specific account type like "CheckingAccount".
  * @description Returns a boolean indicating if the passed account is of type "unlinked".
  * @example const isLOC = Account.isLOC(account);
  * @memberof Account 
  */
  static isUnlinked(account) {
    return account.type === "unlinked";
  }

  /** 
  * @function formatAccountNumber   
  * @static
  * @property {STRING} number - An account number you want to mask.
  * @description Returns a masked account number, like "*1234".
  * @example const formattedAccountNumber = Account.formatAccountNumber("1234");
  * @memberof Account 
  */
  static formatAccountNumber(number) {
    const { options } = this._GlobalController.state.branding;
    const mask = options.account_mask_char_abi22;
    var numLen = number.length;
    var res = "";

    if(numLen === 4) {
      res = `${ mask }${ number }`;
    } else if(numLen > 4) {
      var diff = numLen - 4;
      res = number.slice(diff);
      res = `${ mask }${ res }`;
    } else {
      var diff = 4 - numLen;
      var chars = new Array(diff + 1).join("1");
      res = `${ mask }${ chars }${ number }`;
    }

    return res;
  }

  /** 
  * @function getNextMaskedAccountNumber   
  * @static
  * @property {VOID} - No parameters.
  * @description Returns a masked account number, like "*1234", that is globally unique.
  * @example const newMaskedAccountNumber = Account.getNextMaskedAccountNumber();
  * @memberof Account 
  */
  static getNextMaskedAccountNumber() {
    const { options } = this._GlobalController.state.branding;
    const mask = options.account_mask_char_abi22;
    let number = Account.getNextAccountNumber();
    let numLen = number.length;

    // Will validate that a number has a length of 4. If not
    // then it will remove from start (e.g. 12345 -> 2345) or prefix (e.g. 0 -> 0000).
    const validateIncrementedAccNum = (useNum, prepend) => {
      let curr = String(useNum);
      const numLen = useNum.length;

      if(numLen > 4) {
        const diff = numLen - 4;
        curr = curr.slice(diff);
      } else if(numLen < 4) {
        var diff = 4 - numLen;
        var chars = new Array(diff + 1).join(prepend);
        curr = `${ chars }${ curr }`;
      }
      return curr;
    }

    number = validateIncrementedAccNum(number, "1");
    
    let currNum = parseFloat(number);

    const allAccounts = Account.initAllIncludingUnlinked();
    // Return all the account numbers ascending.
    const allAccountNumbers = allAccounts.reduce((acc, item) => {
      const num = parseFloat(item.number.replace(/[x*]/g, ''))
      acc.push(num)
      return acc;
    }, [])
    .sort((a, b) => a - b);

    let numAlreadyExists = true;
    let didIncrementNum = false;

    // We are checking that this number is unique.
    while(numAlreadyExists) {
      const alreadyExists = allAccountNumbers.filter(item => {
        return item === currNum
      }).length > 0;

      if(alreadyExists) {
        currNum += 1;
        didIncrementNum = true;
      } else {
        numAlreadyExists = false;
      }
    }

    currNum = String(currNum);
    numLen = currNum.length;

    // This code handles the edge case where "9999" was entered, which incremented to "10000",
    // which we then sliced to "0000" and converted to int 0 above.
    currNum = validateIncrementedAccNum(currNum, "0");

    const res = `${ mask }${ currNum }`;

    return String(res);
  }

  /** 
  * @function getNextAccountNumber   
  * @static
  * @property {VOID} - No parameters.
  * @description Returns the next account number that is globally unique.
  * If the last account number in the model is "1234", this function returns "1235".
  * @example const nextAccountNumber = Account.getNextAccountNumber();
  * @memberof Account 
  */
  static getNextAccountNumber() {
    const allAccounts = Account.initAllIncludingUnlinked();
    // Return all the account numbers ascending.
    const allAccountNumbers = allAccounts.reduce((acc, item) => {
      const num = parseFloat(item.number.replace(/[x*]/g, ''))
      acc.push(num)
      return acc;
    }, [])
    .sort((a, b) => a - b);

    const lastAccount = allAccountNumbers[allAccountNumbers.length - 1]
    let lastAccountNumber = lastAccount + 1;

    return String(lastAccountNumber);
  }

  /** 
  * @function getNewAccountOfType   
  * @static
  * @property {STRING} type - An account type, like "unlinked".
  * @property {Account} account - An account instance, like Account.init({}).
  * @property {AccountByType} accountByType - An account instance by type, like UnlinkedAccount.init({}).
  * @property {STRING} id - A new unique account id.
  * @property {STRING} number - An account number, like "1234567890".
  * @property {STRING} name - An account name, like "Benjamin".
  * @property {STRING} nickname - An account nickname, like "Benjamin".
  * @description Returns a new instance account by type, like "UnlinkedAccount".
  * This function currently only supports account type "UnlinkedAccount".
  * @example const newUnlinkedAccount = Account.getNewAccountOfType(
      BCMSDefaultModel.account(),
      BCMSDefaultModel.unlinkedAccount(),
      "unlinked",
      "123", 
      "*1234",
      "some name",
      "some nickname"
  );
  * @memberof Account 
  */
  static getNewAccountOfType(type, account, accountByType, id, number, name, nickname) {
    const context = this._GlobalController;
    const nextID = id;
    const defaultAcc = account; // ex. BCMSDefaultModel.account();
    const defaultUnlinkedAcc = accountByType; // ex. BCMSDefaultModel.unlinkedAccount();
    const newAccountNumber = number;
    defaultUnlinkedAcc.id = nextID;
    defaultUnlinkedAcc.mapID = nextID;
    defaultAcc.id = nextID;
    defaultAcc.type = type;
    defaultAcc.number = Account.formatAccountNumber(newAccountNumber);
    defaultAcc.nickname = nickname;
    defaultAcc.name = name;

    const newRecords = [
        Account.create(defaultAcc.record),
        UnlinkedAccount.create(defaultUnlinkedAcc.record)
    ];

    BCMSRecord.batch(newRecords, context, Classes);
    return UnlinkedAccount.initByID(nextID);
  }
}

Account.define({
  "recordType": "accounts",
  "recordIDProp": "id",
  "className" : "Account",
  "recordProperties": [

   /** 
    * @name id
    * @instance
    * @property get {NUMBER} - gets the id of the account
    * @property set {NUMBER} - sets the id of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("id", "_id"),

    /** 
     * @name name
     * @instance
     * @property get {STRING} - gets the name of the account
     * @property set {STRING} - sets the name of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("name", "_name"),

    /** 
     * @name nickname
     * @instance
     * @property get {STRING} - gets the nickname of the account
     * @property set {STRING} - sets the nickname of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("nickname", "_nickname"),

   /** 
    * @name number
    * @instance
    * @property get {STRING} - gets the number of the account
    * @property set {STRING} - sets the number of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("number", "_number"),

   /** 
    * @name type
    * @instance
    * @property get {STRING} - gets the type of the account
    * @property set {STRING} - sets the type of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToString("type", "_type"),

    /** 
     * @name canTransferFrom
     * @instance
     * @property get {STRING} - gets the typeID of the account
     * @property set {STRING} - sets the typeID of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("canTransferFrom", "_canTransferFrom"),

   /** 
    * @name dateOpened
    * @instance
    * @property get {bcmsDate} - gets the dateOpened of the account
    * @property set {bcmsDate} - sets the dateOpened of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToDate("dateOpened", "_dateOpened"),

    /** 
     * @name balanceLabel
     * @instance
     * @property get {STRING} - gets the balanceLabel of the account
     * @property set {STRING} - sets the balanceLabel of the account
     * @memberof Account 
     */

    BCMSRecordProperty.mapToString("balanceLabel", "_balanceLabel"),

    /**
     * @name secondaryBalanceLabel
     * @instance
     * @property get {STRING} - gets the secondaryBalanceLabel of the account
     * @property set {STRING} - sets the secondaryBalanceLabel of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("secondaryBalanceLabel", "_secondaryBalanceLabel"),

  /** 
    * @name hasFastBalance
    * @instance
    * @property get {BOOLEAN} - gets the hasFastBalance of the account
    * @property set {BOOLEAN} - sets the hasFastBalance of the account
    * @memberof Account 
    */   

    BCMSRecordProperty.mapToBoolean("hasFastBalance", "_hasFastBalance"),

   /** 
    * @name isMobilePrimary
    * @instance
    * @property get {BOOLEAN} - gets the isMobilePrimary of the account
    * @property set {BOOLEAN} - sets the isMobilePrimary of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToBoolean("isMobilePrimary", "_isMobilePrimary"),

   /** 
    * @name lastDownloadDate
    * @instance
    * @property get {bcmsDate} - gets the lastDownloadDate of the account
    * @property set {bcmsDate} - sets the lastDownloadDate of the account
    * @memberof Account 
    */

    BCMSRecordProperty.mapToDate("lastDownloadDate", "_lastDownloadDate"),

    /**
     * @name statementPreference
     * @instance
     * @property get {STRING} - gets the statementPreference of the account
     * @property set {STRING} - sets the statementPreference of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("statementPreference", "_statementPreference"),

    /**
     * @name isExternal
     * @instance
     * @property get {STRING} - gets the external value of the account
     * @property set {STRING} - sets the external value of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToBoolean("isExternal", "_isExternal"),

    /**
     * @name institutionName
     * @instance
     * @property get {STRING} - gets the institution name of the account
     * @property set {STRING} - sets the institution name of the account
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("institutionName", "_institutionName"),

    /** 
    * @name routingNumber
    * @instance
    * @property get {NUMBER} - gets the routing number of the account
    * @property set {NUMBER} - sets the routing number of the account
    * @memberof Account 
    */
    BCMSRecordProperty.mapToNumber("routingNumber", "_routingNumber"),

    /**
     * @name externalStatus
     * @instance
     * @property get {STRING} - gets the external status of the account, like "active".
     * @property set {STRING} - sets the external status of the account, like "active".
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("externalStatus", "_externalStatus"),

    /**
     * @name isBusiness
     * @instance
     * @property get {STRING} - gets the isBusiness value
     * @property set {STRING} - sets the isBusiness value
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("isBusiness", "_isBusiness"),

    /**
     * @name profile
     * @instance
     * @property get {Profile} - gets the profile value
     * @property set {Profile} - sets the profile value
     * @memberof Account
     */
    BCMSRecordProperty.mapToClass("profile", "_profile", () => Profile),

    /**
     * @name showDesktopAvailableBalance
     * @instance
     * @property get {STRING} - gets the showDesktopAvailableBalance value
     * @property set {STRING} - sets the showDesktopAvailableBalance value
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("showDesktopAvailableBalance", "_showDesktopAvailableBalance"),

    /**
     * @name showDesktopCurrentBalance
     * @instance
     * @property get {STRING} - gets the showDesktopCurrentBalance value
     * @property set {STRING} - sets the showDesktopCurrentBalance value
     * @memberof Account
     */
    BCMSRecordProperty.mapToString("showDesktopCurrentBalance", "_showDesktopCurrentBalance"),

    /**
     * @name showMobileAvailableBalance
     * @instance
     * @property get {STRING} - gets the showMobileAvailableBalance value
     * @property set {STRING} - sets the showMobileAvailableBalance value
     * @memberof Account
     */
      BCMSRecordProperty.mapToString("showMobileAvailableBalance", "_showMobileAvailableBalance"),

      /**
       * @name showMobileCurrentBalance
       * @instance
       * @property get {STRING} - gets the showMobileCurrentBalance value
       * @property set {STRING} - sets the showMobileCurrentBalance value
       * @memberof Account
       */
      BCMSRecordProperty.mapToString("showMobileCurrentBalance", "_showMobileCurrentBalance"),
  ]
})




